/**
 * Biometric unlock for the native shells (Face ID / Touch ID / Android fingerprint).
 *
 * The website must behave exactly as before, so every entry point returns a
 * "not available" answer off-native and the plugin is only ever reached through
 * a dynamic import inside `getPlugin()` — it never lands in the web bundle's
 * startup path.
 *
 * WHAT IS STORED, AND WHY IT IS NOT A PASSWORD.
 *
 * The secure store holds a server-issued DEVICE TOKEN, never the password. The
 * plugin's keychain item on Capacitor 7 is a plain SecItemAdd — no
 * kSecAccessControl, backup-eligible, and on Android a key that does not set
 * setUserAuthenticationRequired. The biometric check is therefore ours, enforced
 * in this file, not the operating system's, enforced on the item. A password
 * behind a check like that is a password at rest on a phone; a device token is
 * not, because it signs in one device, rotates on every single use, and can be
 * revoked from a browser without changing the password.
 *
 * So `setCredentials`' "password" slot holds `dt1:<token>` — the marker matters,
 * see `purgeLegacyBiometricEntry` below.
 *
 * Only non-secret flags — which email has it enabled, which emails declined the
 * offer — are kept in localStorage. Nothing here logs a token.
 */
import { Capacitor } from '@capacitor/core';

/** Keychain `server` / Keystore key alias the credentials are filed under. */
const SERVER = 'ie.ncad.cdbooking';
const ENABLED_KEY = 'ncad.biometric.enabledEmail';
const DECLINED_KEY = 'ncad.biometric.declinedEmails';
/**
 * The device_tokens row id this phone currently holds. Not a secret — it is the
 * handle the profile uses to revoke THIS device and to mark it in the list, and
 * it changes on every rotation. The token itself never goes near localStorage.
 */
const TOKEN_ID_KEY = 'ncad.biometric.tokenId';

/**
 * Marks a stored value as a device token. Anything in the store WITHOUT this
 * prefix was written by the earlier build that saved the real password, and is
 * deleted on sight rather than used — see `purgeLegacyBiometricEntry`.
 */
const DEVICE_TOKEN_PREFIX = 'dt1:';

/** Thrown out of `unlockDeviceToken` when the entry is an old password one. */
export const LEGACY_ENTRY = 'BIOMETRIC_LEGACY_ENTRY';

const BIOMETRY_LABELS = {
  1: 'Touch ID',
  2: 'Face ID',
  3: 'fingerprint',
  4: 'face unlock',
  5: 'iris unlock',
};

const normalise = (email) => (email || '').trim().toLowerCase();

const readJSON = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

export const isNativeApp = () => Capacitor.isNativePlatform();

/**
 * Resolves the plugin on native only. Web callers get null and never import it.
 *
 * The proxy comes back wrapped in an object deliberately. Capacitor's plugin
 * proxy answers to every property name, `then` included, so returning it bare
 * from an async function makes the runtime treat it as a thenable and wait on a
 * native "then" call that never arrives — the sign-in simply hangs.
 */
async function getPlugin() {
  if (!isNativeApp()) return null;
  try {
    const mod = await import('@capgo/capacitor-native-biometric');
    return mod.NativeBiometric ? { api: mod.NativeBiometric } : null;
  } catch {
    return null;
  }
}

/** Wording for the button and the offer, e.g. "Face ID" or "fingerprint". */
const biometryLabel = (type) => BIOMETRY_LABELS[type] || 'biometrics';

/** What the profile's "Signed-in devices" list will call this device. */
export function describeDevice() {
  const platform = (Capacitor.getPlatform && Capacitor.getPlatform()) || 'web';
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  let device_name = 'This device';
  if (/iPad/i.test(ua)) device_name = 'iPad';
  else if (/iPhone/i.test(ua)) device_name = 'iPhone';
  else if (/Android/i.test(ua)) device_name = 'Android phone';
  return { device_name, platform };
}

export async function getBiometricAvailability() {
  const plugin = await getPlugin();
  if (!plugin) return { available: false, label: null };
  try {
    // useFallback:false — a device passcode is not what we are offering here,
    // so an unenrolled device must report unavailable.
    const result = await plugin.api.isAvailable({ useFallback: false });
    return {
      available: Boolean(result?.isAvailable),
      label: biometryLabel(result?.biometryType),
    };
  } catch {
    return { available: false, label: null };
  }
}

export function enabledEmail() {
  try {
    return localStorage.getItem(ENABLED_KEY);
  } catch {
    return null;
  }
}

const hasDeclinedBiometric = (email) =>
  readJSON(DECLINED_KEY, []).includes(normalise(email));

export function rememberBiometricDeclined(email) {
  const list = readJSON(DECLINED_KEY, []);
  const account = normalise(email);
  if (account && !list.includes(account)) {
    try {
      localStorage.setItem(DECLINED_KEY, JSON.stringify([...list, account]));
    } catch { /* a full or blocked store just means we may ask again */ }
  }
}

function forgetDecline(email) {
  const account = normalise(email);
  const list = readJSON(DECLINED_KEY, []).filter((e) => e !== account);
  try {
    localStorage.setItem(DECLINED_KEY, JSON.stringify(list));
  } catch { /* non-fatal */ }
}

/** Should we ask this account, once, whether to turn biometric sign-in on? */
export async function shouldOfferBiometric(email) {
  const account = normalise(email);
  if (!isNativeApp() || !account) return { offer: false, label: null };
  if (hasDeclinedBiometric(account) || enabledEmail() === account) {
    return { offer: false, label: null };
  }
  const { available, label } = await getBiometricAvailability();
  return { offer: available, label };
}

/**
 * Put a device token in the secure store and mark the account enabled.
 *
 * This is the ONLY writer of the store, and it is not capable of writing a
 * password: what it stores is always `dt1:` + what it was handed. It replaces
 * the old `enableBiometricLogin(email, password)` outright rather than being
 * added beside it, so no call site can still be passing a password.
 */
export async function storeDeviceToken(email, deviceToken, tokenId = null) {
  const plugin = await getPlugin();
  const account = normalise(email);
  if (!plugin || !account || !deviceToken) return false;
  await plugin.api.setCredentials({
    username: account,
    password: `${DEVICE_TOKEN_PREFIX}${deviceToken}`,
    server: SERVER,
  });
  try {
    localStorage.setItem(ENABLED_KEY, account);
    if (tokenId) localStorage.setItem(TOKEN_ID_KEY, String(tokenId));
  } catch { /* flags are a convenience; the token is already stored */ }
  forgetDecline(account);
  return true;
}

/** The device_tokens row this phone holds, if we know it. */
export function storedDeviceTokenId() {
  try {
    const raw = localStorage.getItem(TOKEN_ID_KEY);
    return raw ? Number(raw) : null;
  } catch {
    return null;
  }
}

/** Removes the stored token and the flag. Safe to call when neither exists. */
export async function disableBiometricLogin() {
  const plugin = await getPlugin();
  if (plugin) {
    try {
      await plugin.api.deleteCredentials({ server: SERVER });
    } catch { /* nothing stored, or already gone */ }
  }
  try {
    localStorage.removeItem(ENABLED_KEY);
    localStorage.removeItem(TOKEN_ID_KEY);
  } catch { /* non-fatal */ }
}

/**
 * Wipe an entry left by the build that stored a real password.
 *
 * Run at startup. That build exists on at least one phone, and its keychain item
 * is indistinguishable from a current one except for the missing `dt1:` marker.
 * The entry is DELETED, not upgraded: upgrading would mean signing in with the
 * stored password to trade it for a token, which is the exact act this change
 * exists to stop. The user signs in once with their password and is offered
 * biometrics again.
 *
 * Reading the item without a biometric prompt is not a shortcut — it is the
 * weakness described at the top of this file, and the only good use for it is
 * getting rid of the item.
 */
export async function purgeLegacyBiometricEntry() {
  if (!isNativeApp() || !enabledEmail()) return false;
  const plugin = await getPlugin();
  if (!plugin) return false;
  try {
    const stored = await plugin.api.getCredentials({ server: SERVER });
    if (stored?.password?.startsWith(DEVICE_TOKEN_PREFIX)) return false;
  } catch {
    // Nothing readable behind an "enabled" flag is its own kind of stale: clear
    // the flag so the login screen does not offer an unlock that cannot work.
    await disableBiometricLogin();
    return true;
  }
  await disableBiometricLogin();
  return true;
}

/**
 * Prompts for biometrics and, only on success, reads the device token back.
 * Rejects when the user cancels or the check fails — the caller keeps the
 * password form usable.
 */
export async function unlockDeviceToken(label = 'biometrics') {
  const plugin = await getPlugin();
  if (!plugin) throw new Error('Biometric sign-in is not available on this device.');
  await plugin.api.verifyIdentity({
    reason: 'Sign in to NCAD Bookings',
    title: `Sign in with ${label}`,
    subtitle: '',
    description: '',
    useFallback: false,
  });
  const { username, password } = await plugin.api.getCredentials({ server: SERVER });
  if (!password?.startsWith(DEVICE_TOKEN_PREFIX)) {
    // An old password entry that the startup sweep has not reached. It is never
    // sent anywhere; it is deleted here too.
    await disableBiometricLogin();
    const error = new Error('This device needs to be set up again.');
    error.code = LEGACY_ENTRY;
    throw error;
  }
  return { email: username, token: password.slice(DEVICE_TOKEN_PREFIX.length) };
}

/** A biometric check on its own, for turning the setting on while signed in. */
export async function confirmBiometricIdentity(label = 'biometrics') {
  const plugin = await getPlugin();
  if (!plugin) throw new Error('Biometric sign-in is not available on this device.');
  await plugin.api.verifyIdentity({
    reason: 'Turn on biometric sign-in for NCAD Bookings',
    title: `Confirm with ${label}`,
    subtitle: '',
    description: '',
    useFallback: false,
  });
  return true;
}

/** A failed sign-in is only a "token rejected" if we actually reached the server. */
export const isNetworkError = (error) =>
  /failed to fetch|networkerror|load failed|network request failed|could not connect/i
    .test(error?.message || '');
